/**
 * What a lead looks like to a component.
 *
 * Two things the API shape does not give directly and every screen needs:
 * `slaState`, because a raw due-date makes each agent do date arithmetic in
 * their head; and `assigneeName`, because the API carries a userId and a
 * possibly-absent hydrated user.
 */
export enum SlaState {
  /** Answered inside the target. */
  MET = 'met',
  /** Answered after the target, or unanswered past it. */
  BREACHED = 'breached',
  /** Unanswered, past the at-risk ratio but not yet due. */
  AT_RISK = 'at_risk',
  /** Unanswered and still comfortably inside the target. */
  AWAITING = 'awaiting',
  /** No target recorded — an older lead, or one created before SLAs existed. */
  UNKNOWN = 'unknown'
}

export interface LeadUiModel {
  id: string;
  shortId: number;
  vertical: string;
  details: Record<string, unknown>;

  name: string;
  phone: string;
  email: string;
  message: string;

  source: string;
  status: string;
  resolutionType?: string;
  notQualifiedReason?: string;
  notInterestedReason?: string;
  pendingReason?: string;
  resolutionNote?: string;
  resolvedAt?: Date;

  marketingConsent: boolean;
  consentSource?: string;
  consentAt?: Date;

  marketingChannel?: string;
  messagingChannel?: string;
  adType?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  firstTouchChannel?: string;
  firstTouchCampaign?: string;

  assignedToId?: string;
  /** The assignee's name, or their raw id when hydration could not resolve it. */
  assigneeName?: string;
  assignedAt?: Date;

  contactAttempts: number;
  remainingContactAttempts: number;
  maxContactAttempts: number;

  slaState: SlaState;
  slaDueAt?: Date;
  firstResponseAt?: Date;

  qualityScore?: number;
  valueCategory?: string;

  lastComment?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface LeadActivityUiModel {
  id: string;
  activityType: string;
  description: string;
  /** Absent means the system did it — an auto-resolution or a stale sweep. */
  actorName?: string;
  isSystem: boolean;
  createdAt: Date;
}

export interface LeadCommentUiModel {
  id: string;
  text: string;
  authorId: string;
  authorName: string;
  createdAt: Date;
  updatedAt: Date;
}
